define([
	"dgrid/OnDemandGrid",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dom-style",
	"dojo/fx",
	"dojo/when",
	"csi/html",
	"csi/utility/stringUtility",
], function(OnDemandGrid, declare, lang, aspect, domStyle, fx, when, html, {stringSubstitute}) {

	return declare(OnDemandGrid, {
		// summary:
		//		An extension to OnDemandGrid to allow sliding in new result set (i.e. children)
		// 		with ability to go back to previous result set. Only the grid content will slide - the rest
		// 		(header, footer) will remain fixed. If requiring a grid with selection, this extension is only
		// 		compatible with the CellSelection extension. It is also not compatible with the ColumnSet mixin.

		// selectOnSlide: Boolean
		//		If true (default) then remembers selection when sliding and restores when it slides back out.
		//		This only works when selectionMode is single.
		selectOnSlide: true,

		// slideDoubleClickDelay: Number
		//		Delay to use for detecting double-click
		slideDoubleClickDelay: 300,

		// slideInTitle: String
		//		Title for button to slide in to next level
		slideInTitle: "Expand",

		// slideOutTitle: String
		//		Title for button to slide out to previous level
		slideOutTitle: "Back to ${0} (double-click to return to top)",

		// slideOutRootTitle: String
		//		Title for button to return to slide out to top level
		slideOutRootTitle: "Back to top",

		// slideRootLabel: String
		//		Label to display for top result set.
		//slideRootLabel: "",

		// slideLabelField: String
		//		Field to use to get label for item when sliding to child result set. Defaults to first column field.
		//slideLabelField: "",

		// slideOnSelect: Boolean
		//		If true, instead of selecting elements clicking them just makes them slide
		slideOnSelect: false,

		//_slideColumnId: null,
		//_slideHeader: null,
		//_slideOutTimeout: null,
		//_slideSelect: null,
		//_slideStack: null,

		// _subWidget: Object
		//		If the slideGrid has a subWidget, it will be displayed instead of a normal list
		//_subWidget: null

		onSlideIn: function(item, obj) {
			// summary:
			//		Extension point to get information to push onto stack for current result set
		},

		onSlideOut: function(obj) {
			// summary:
			//		Extension point to handle switching back to former result set.
		},

		postCreate: function() {
			this.inherited(arguments);
			this.selectionDelegate = ".dgrid-cell:not(.dgrid-column-sliiide)";
			this._slideStack = [];

			if (this.selectionMode !== "single") {
				this.selectOnSlide = false;
			}

			this.on(".csi-dgrid-slide-left:click", lang.hitch(this, this._slideOut));
			this.on(".csi-dgrid-slide-right:click", lang.hitch(this, this._slideIn));

			if (this.slideOnSelect) {
				this.on(".dgrid-cell:click", lang.hitch(this, this._slideIn));
			}
		},

		refresh: function() {
			// summary:
			//		Content is cleared every refresh, so need to reset slide header node
			this._slideHeader = null;
			this.inherited(arguments);
		},

		renderArray: function(results, beforeNode, options) {
			// summary:
			//		Render slide header if necessary.
			if (!this._slideHeader) {
				var first = this._slideStack[0];
				var back = this._slideStack[1];
				var label = first ? first.label : this.slideRootLabel;
				if (label) {
					this._slideHeader = html.create("div", {"class": "csi-dgrid-slide-header"}, beforeNode, "before");

					if (first) {
						var backTitle = back ? stringSubstitute(this.slideOutTitle, [back.label]) : this.slideOutRootTitle;

						html.create("div", {
							"class": "dijitInline csiActionIcon material-icons csi-dgrid-slide-left",
							innerText: "chevron_left",
							title: backTitle
						}, this._slideHeader);
					}

					html.create("div", {
						"class": "dijitInline csi-dgrid-slide-label",
						innerText: label,
						title: label
					}, this._slideHeader);
				}
			}

			if (this.store.isSubWidget) {
				var subWidgetAttach = html.create("div", {}, this._slideHeader, "after");

				this._subWidget = new this.store.subWidgetClass({}, subWidgetAttach);
			}

			var rows = this.inherited(arguments);
			var s = this._slideSelect;
			if (s) {
				this._slideSelect = null;

				// basically taken from Selection.js - can't use its version because it will only reselect
				// what is already selected in grid.selection, and in that case it does not fire new
				// selection events
				when(rows, lang.hitch(this, function(rows) {
					if (!rows.length) return;

					if (s === true) {
						this.select(this.cell(rows[0], this._slideColumnId));
					} else {
						for (var i = 0; i < rows.length; i++) {
							if (s[this.row(rows[i]).id]) {
								this.select(this.cell(rows[i], this._slideColumnId));
							}
						}
					}
				}));
			}

			return rows;
		},

		slideIn: function(/*Object*/item, /*Store*/store) {
			// summary:
			//		Slides in new content from selected item.
			var obj = {
				label: item[this.slideLabelField],
				store: this.store
			};

			if (this._slideColumnId && this.selection) {
				obj.selection = lang.clone(this.selection);
			}

			if (this.clearSelection) {
				this.clearSelection();
			}

			if (this._slideColumnId && (this.selectionMode == "single")) {
				this._slideSelect = true;
			}

			this.onSlideIn(item, obj);
			this._slideStack.unshift(obj);
			this._slide(store, false);
		},

		slideOut: function() {
			// summary:
			//		Slides back to previous content.
			var obj = this._slideStack.shift();
			if (obj) {
				if (this.clearSelection) {
					this.clearSelection();
				}

				if (this._slideColumnId && obj.selection) {
					this._slideSelect = lang.clone(obj.selection);
				}

				this._slide(obj.store, true);
				this.onSlideOut(obj);
			}
		},

		/**
		 * Slide to specific top-level menu item, to show it's submenu
		 * @param {string} id - the top-level menu to slide to
		 */
		slideTo: function(id) {
			var item = this.store.get(id);
			if (item) {
				this._slideTo(item);
			}
		},

		slideToTop: function(/*Object?*/selection) {
			// summary:
			//		Slides back to top, with option of changing original selection.
			var top = this._slideStack.last();
			if (top) {
				if (selection) top.selection = selection;
				this._slideStack = [top];
			}

			this.slideOut();
		},

		_configColumns: function(prefix, rowColumns) {
			// summary:
			//		Update columns definition to have one more column to support slide action
			var column = {
				id: "sliiide",
				label: "",
				renderCell: lang.hitch(this, this._renderCellSlide),
				sortable: false
			};

			if (rowColumns instanceof Array) {
				rowColumns.push(column);
			} else {
				rowColumns[column.id] = column;
			}

			var subRows = this.inherited(arguments);
			var column, id;

			for (id in subRows) {
				column = subRows[id];
				this.slideLabelField = this.slideLabelField || column.field;

				if (column.field == this.slideLabelField) {
					if (this.selectOnSlide) {
						this._slideColumnId = id;
					}
					break;
				}
			}

			return subRows;
		},

		_renderCellSlide: function(obj, value, node, options) {
			// summary:
			//		Render icon to slide in to next level
			if (this.store.maySlide && this.store.maySlide(obj)) {
				return html.create("div", {
					"class": "dijitInline csiActionIcon material-icons csi-dgrid-slide-right",
					innerText: "chevron_right",
					title: this.slideInTitle
				}, this._slideHeader);
			}

			return null;
		},

		_slide: function(/*Store*/store, /*Boolean*/bOut) {
			// summary:
			//		Refreshes grid with new store and slides in/out new content
			var bn = this.bodyNode;
			var cn = this.contentNode;
			var h = domStyle.get(bn, "height");
			var w = domStyle.get(this.domNode.parentNode, "width");
			var sh = this._slideHeader;

			// if sliding in, then slide to left; otherwise slide to right
			if (bOut) w = -w;

			domStyle.set(bn, {
				overflow: "visible",
				position: "relative"
			});

			// move real grid content off screen
			domStyle.set(cn, {
				height: h + "px",
				left: w + "px",
				top: "0",
				position: "absolute"
			});

			// show copy of grid content in usual content position
			var cnClone = cn.cloneNode(true);
			bn.insertBefore(cnClone, cn);
			domStyle.set(cnClone, "left", "0");

			// refresh grid content with new store
			var query = store.getSlideQuery ? store.getSlideQuery() : {};
			var options = store.getSlideOptions ? store.getSlideOptions() : {};
			this.set("store", store, query, options);

			var anim = fx.combine([
				fx.slideTo({node: cnClone, left: -w}),
				fx.slideTo({node: cn, left: 0})
			]);

			// slide out copy and slide in new grid content
			aspect.after(anim, "onEnd", function() {
				html.destroyElement(cnClone);

				domStyle.set(cn, {
					height: "",
					left: "",
					top: "",
					position: ""
				});

				domStyle.set(bn, {
					overflow: "",
					position: ""
				});
			});

			anim.play();
		},

		_slideIn: function(evt) {
			var grid = this;
			var cell = this.cell(evt.target);
			var item = cell.row.data;

			if (!this._slideTo(item) && evt.target.classList.contains("csi-dgrid-slide-right")) {
				// next level does not exist, so remove icon
				html.destroyElement(evt.target);
			}
		},

		_slideOut: function(evt) {
			// summary:
			// 		Slide back to previous level, or to top level if double-click detected
			if (this._slideOutTimeout) {
				clearTimeout(this._slideOutTimeout);
				this._slideOutTimeout = null;
				this.slideToTop();
				return;
			}

			this._slideOutTimeout = setTimeout(lang.hitch(this, function() {
				this._slideOutTimeout = null;
				this.slideOut();
			}), this.slideDoubleClickDelay);
		},

		_slideTo: function(item) {
			var grid = this;
			var slideStore = this.store.getSlideStore ? this.store.getSlideStore(item) : null;

			if (slideStore) {
				if (slideStore.isSubWidget && slideStore.loadImages) {
					slideStore.loadImages();
				}
				// wait for query to finish before sliding
				when(slideStore.query(), function(results) {
					grid.slideIn(item, slideStore);
				});

				return true;
			}

			return false;
		}
	});
});
